import React from 'react';
import { BsArrowRight, BsBriefcase, BsLightbulb, BsPeople } from 'react-icons/bs';
import CtaButton from '../../components/common/CtaButton';
import './Careers.css';

const CAREER_PILLARS = [
  {
    number: '01',
    Icon: BsBriefcase,
    title: 'Work on real problems',
    description:
      'Our projects start with a business need. The goal is to make the finished product genuinely useful to the people who rely on it.',
  },
  {
    number: '02',
    Icon: BsPeople,
    title: 'Think as a team',
    description:
      'Good work comes from sharing context early, asking clear questions, and making room for different points of view.',
  },
  {
    number: '03',
    Icon: BsLightbulb,
    title: 'Stay curious',
    description:
      'We value people who care about their craft, learn from the work, and look for a simpler way when one is possible.',
  },
];

const CAREERS_EMAIL = 'careers@seikural.com';
const CAREERS_MAILTO = `mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(
  'Career interest at Seikural Innovation'
)}`;

const Careers = () => (
  <div className="careers-page">
    <section className="careers-hero py-5 border-bottom tw-bg-white" aria-labelledby="careers-heading">
      <div className="container py-lg-5">
        <div className="row justify-content-center text-center">
          <div className="col-12 col-lg-9 col-xl-8">
            <p className="careers-eyebrow text-uppercase fw-bold small mb-3 tw-text-sky-600">
              Careers <span className="mx-2 tw-text-slate-400">/</span> Seikural Innovation
            </p>
            <h1 id="careers-heading" className="careers-title display-4 fw-bold mb-4 tw-text-slate-900">
              Good work is built by people who care.
            </h1>
            <p className="careers-intro mb-4 mx-auto tw-text-slate-600">
              We build software for the way businesses actually work. If you like
              thoughtful problem-solving, honest collaboration, and making
              technology useful, we would be glad to hear from you.
            </p>
            <div className="d-flex justify-content-center">
              <CtaButton href={CAREERS_MAILTO} variant="primary">
                Write to our team
              </CtaButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-5 tw-bg-[#f8f4ff]" aria-labelledby="careers-approach-heading">
      <div className="container py-lg-4">
        <div className="row align-items-end g-3 mb-4 mb-lg-5">
          <div className="col-12 col-lg-7">
            <p className="text-uppercase fw-bold small mb-2 tw-text-sipl-teal">A little about the work</p>
            <h2 id="careers-approach-heading" className="h1 fw-bold mb-0 tw-text-slate-900">
              Thoughtful work, done together.
            </h2>
          </div>
          <div className="col-12 col-lg-5">
            <p className="mb-0 tw-text-slate-600">
              Our work spans web applications, business software, product
              design, and cloud services. The common thread is a practical
              approach and respect for the people using what we build.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {CAREER_PILLARS.map(({ number, Icon, title, description }) => (
            <div className="col-12 col-md-6 col-lg-4" key={number}>
              <article className="careers-pillar h-100 p-4 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <span className="careers-pillar-number fw-bold tw-text-slate-400">{number}</span>
                  <Icon className="careers-pillar-icon tw-text-sipl-teal" aria-hidden="true" />
                </div>
                <h3 className="h5 fw-bold mb-2 tw-text-slate-900">{title}</h3>
                <p className="mb-0 tw-text-slate-600">{description}</p>
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="careers-opportunities py-5 tw-bg-white" aria-labelledby="careers-openings-heading">
      <div className="container py-lg-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9">
            <p className="text-uppercase fw-bold small mb-2 tw-text-sipl-teal">Open positions</p>
            <h2 id="careers-openings-heading" className="h2 fw-bold mb-3 tw-text-slate-900">
              No roles to share just yet.
            </h2>
            <p className="mb-0 tw-text-slate-600">
              Even so, we are happy to hear from people who connect with our
              work. Send a short introduction, your resume, and links to any
              relevant projects. We will reach out if a suitable opportunity
              becomes available.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="careers-application py-5 tw-bg-[#f8f4ff]" aria-labelledby="careers-application-heading">
      <div className="container py-lg-3">
        <div className="row align-items-center g-3">
          <div className="col-12 col-lg-8">
            <h2 id="careers-application-heading" className="h4 fw-bold mb-2 tw-text-slate-900">
              Have something you would like us to see?
            </h2>
            <p className="mb-0 tw-text-slate-600">
              Share your resume and a little about the work you enjoy.
            </p>
          </div>
          <div className="col-12 col-lg-4">
            <a
              href={CAREERS_MAILTO}
              className="careers-email-link d-inline-flex align-items-center gap-2 fw-semibold text-decoration-none tw-text-sipl-navy"
            >
              {CAREERS_EMAIL}
              <BsArrowRight aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Careers;
